import React, { useEffect, useState, useContext } from "react";
import { createContext } from "use-context-selector";
import {
    getQuestionnaireBySignDocIdAPI,
    createOrUpdateQuestionnaireQuestionAPI,
    deleteQuestionnaireQuestionAPI,
    saveQuestionnaireAPI
} from "../../Services/api";
import { get, cloneDeep, find, map } from "lodash";
import { questionnaireQuestionTypes, questionsTypesToShowOptions } from "../../Components/Questionnaires/QuestionnaireConstants";
import { I18nReactContext } from "../I18nContext";
import useDebounce from "../../Hooks/useDebounce";
import { 
    getQuestionnaireDefaults, 
    createQuestion as createQuestionHelper, 
    createOption as createOptionHelper, 
    validateQuestionnaireQuestions 
} from "./QuestionnaireContextHelper";
import { QuestionnaireSessionReactContextProvider } from "./QuestionnaireSessionContext";

/**
 * Questionnaire Context
 * Handles questionnaire management functionality
 */
export const QuestionnaireReactContext = createContext({
    // Question pool state
    questionnaire: {},
    questionnaireQuestions: [],
    activeQuestionId: 0,

    // Question pool management
    initQuestionPool: async ({ signDocId }) => {},
    saveQuestionPool: async () => {},
    setActiveQuestion: () => {},

    // Question management
    updateQuestion: ({ text, type, options = [], ratingScaleConfig = {} }) => {},
    addQuestion: async () => {},
    deleteQuestion: async () => {},
    duplicateQuestion: async () => {},
    getQuestionById: ({ questionId }) => {},

    // Option management
    updateOptionText: () => {},
    toggleCorrectAnswer: () => {},
    addOption: () => {},
    removeOption: () => {},
});

// Context Provider Component
export const QuestionnaireReactContextProvider = ({ children }) => {
    // Questionnaire Creation State
    const [questionnaire, setQuestionnaire] = useState({});
    const [questionnaireQuestions, setQuestionnaireQuestions] = useState([]);
    const [activeQuestionId, setActiveQuestionId] = useState(() => {
        const firstQuestion = questionnaireQuestions[0];
        return get(firstQuestion, 'id', undefined);
    });
    const [saveQuestionnaireTriggerId, setSaveQuestionnaireTriggerId] = useState(0);
    const debouncedQuestionnaireSaveTrigger = useDebounce({value: saveQuestionnaireTriggerId, delay: 500});

    const { i18n } = useContext(I18nReactContext);
    
    // Get defaults from helper
    const {
        defaultQuestionText,
        defaultOptionText,
        defaultRatingScaleConfig
    } = getQuestionnaireDefaults({ i18n });

    useEffect(() => {
        if (debouncedQuestionnaireSaveTrigger > 0) {
            handleServerSaveQuestion();
        }
    }, [debouncedQuestionnaireSaveTrigger]);

    /**
     * Clear question pool data
     */
    const clearQuestionPool = () => {
        setQuestionnaireQuestions([]);
        setQuestionnaire({});
        setActiveQuestionId(undefined);
    };

    /**
     * Get question by ID
     * @param {object} params - Parameters object
     * @param {string} params.questionId - Question ID
     * @returns {object|null} - Question object or null if not found
     */
    const getQuestionById = ({ questionId }) => {
        const question = questionnaireQuestions.find(question => question.id === questionId);
        if (!question) {
            return null;
        }
        return question;
    };

    /**
     * Create a new question
     * @param {object} params - Parameters object
     * @param {number} params.position - Position of the question
     * @returns {object} - New question object
     */
    const createQuestion = ({ position = 0 }) => {
        return createQuestionHelper({
            position,
            defaultQuestionText,
            createOptionFn: createOption
        });
    };

    /**
     * Create a new option
     * @param {object} params - Parameters object
     * @param {number} params.position - Position of the option
     * @returns {object} - New option object
     */
    const createOption = ({ position }) => {
        return createOptionHelper({
            position,
            defaultOptionText
        });
    };

    /**
     * Initialize question pool
     * @param {object} params - Parameters object
     * @param {string} params.signDocId - SignDoc ID
     */
    const initQuestionPool = async ({ signDocId }) => {
        clearQuestionPool();
        try {
            const questionnaireData = await getQuestionnaireBySignDocIdAPI({ signDocId, withQuestions: true });
            const serverQuestions = get(questionnaireData, 'questionnaire.questions', []);

            if (serverQuestions.length === 0) {
                let firstQuestion = createQuestion({ position: 0 });
                firstQuestion = await createOrUpdateQuestionnaireQuestionAPI({
                    signDocId,
                    questionId: undefined,
                    isRandomizeAnswersOrder: firstQuestion.isRandomiseAnswersOrder,
                    ratingScaleConfig: firstQuestion.ratingScaleConfig || null,
                    ...firstQuestion
                });
                serverQuestions.push(firstQuestion);
            }

            setQuestionnaire(questionnaireData);
            setQuestionnaireQuestions(serverQuestions);
        } catch (error) {
            console.error("Error initializing question pool:", error);
        }
    };

    /**
     * Save a specific question to the server
     */
    const handleServerSaveQuestion = async () => {
        let question = find(questionnaireQuestions, { id: activeQuestionId });
        if (!question) {
            return;
        }
        try {
            let updatedQuestion = await createOrUpdateQuestionnaireQuestionAPI({
                signDocId: get(questionnaire, 'id'),
                questionId: activeQuestionId,
                text: question.text,
                type: question.type,
                position: question.position,
                points: question.points,
                isRandomizeAnswersOrder: question.isRandomizeAnswersOrder,
                options: question.options,
                ratingScaleConfig: question.ratingScaleConfig
            });

            const updatedQuestions = [...questionnaireQuestions];
            const activeQuestionIndex = updatedQuestions.findIndex(q => q.id === activeQuestionId);
            updatedQuestions[activeQuestionIndex] = updatedQuestion;
            setQuestionnaireQuestions(updatedQuestions);
        } catch (error) {
            console.error("Error saving question:", error);
        }
    };

    /**
     * Save the entire questionnaire
     * @returns {object} - Validation result
     */
    const saveQuestionPool = async () => {
        await handleServerSaveQuestion();
        let validateQuestionnaire = validateQuestionnaireQuestions({ questionnaireQuestions, i18n, questionnaireType: get(questionnaire, 'type') });
        if (validateQuestionnaire.isValid) {
            await saveQuestionnaireAPI({ signDocId: get(questionnaire, 'id') });
            return validateQuestionnaire;
        } else {
            return validateQuestionnaire;
        }
    };

    /**
     * Update question properties
     * @param {object} params - Parameters object
     * @param {string} params.text - Question text
     * @param {string} params.type - Question type
     * @param {Array} params.options - Question options
     * @param {object} params.ratingScaleConfig - Rating scale configuration
     */
    const updateQuestion = ({ text, type, options, ratingScaleConfig, questionMediaUrl }) => {
        const updatedQuestions = [...questionnaireQuestions];
        let activeQuestion = find(updatedQuestions, { id: activeQuestionId });

        if (!activeQuestion) {
            return;
        }

        if (text !== undefined) {
            activeQuestion.text = text || defaultQuestionText;
        }

        if (type !== undefined && type !== activeQuestion.type) {
            activeQuestion.type = type;

            // If the question type has changed to one that requires options, add a default option.
            if (questionsTypesToShowOptions.includes(type) && (!activeQuestion.options || activeQuestion.options.length === 0)) {
                activeQuestion.options = [createOption({ position: 1 })];
            }

            // Specific logic for MULTIPLE_CHOICE: Ensure only one option can be marked as correct.
            if (type === questionnaireQuestionTypes.MULTIPLE_CHOICE) {
                const currentOptions = activeQuestion.options || [createOption({ position: 1 })];
                const firstCorrectIndex = currentOptions.findIndex(option => option.isCorrect);

                const updatedOptions = currentOptions.map((option, i) => ({
                    ...option,
                    isCorrect: i === firstCorrectIndex ? option.isCorrect : false
                }));
                activeQuestion.options = updatedOptions;
            }

            if (type === questionnaireQuestionTypes.RATING_SCALE) {
                activeQuestion.options = [];
                activeQuestion.ratingScaleConfig = defaultRatingScaleConfig;
            } else if (type === questionnaireQuestionTypes.FREE_TEXT) {
                activeQuestion.options = [];
                activeQuestion.ratingScaleConfig = null;
            } else {
                activeQuestion.ratingScaleConfig = null;
            }
        }

        if (options !== undefined) {
            activeQuestion.options = options;
        }

        if (ratingScaleConfig !== undefined) {
            activeQuestion.ratingScaleConfig = ratingScaleConfig;
        }

        // Add handling for questionMediaUrl
        if (questionMediaUrl !== undefined) {
            activeQuestion.questionMediaUrl = questionMediaUrl;
        }

        setQuestionnaireQuestions(updatedQuestions);
        setSaveQuestionnaireTriggerId(prev => prev + 1);
    };

    /**
     * Add a new question
     */
    const addQuestion = async () => {
        const activeQuestionIndex = questionnaireQuestions.findIndex(q => q.id === activeQuestionId);
        const newQuestion = createQuestion({ position: activeQuestionIndex + 1 });

        try {
            let createdQuestion = await createOrUpdateQuestionnaireQuestionAPI({
                signDocId: get(questionnaire, 'id'),
                questionId: undefined,
                isRandomizeAnswersOrder: newQuestion.isRandomiseAnswersOrder,
                ratingScaleConfig: newQuestion.ratingScaleConfig || null,
                ...newQuestion
            });

            const newQuestionsArray = [...questionnaireQuestions];
            newQuestionsArray.splice(activeQuestionIndex + 1, 0, createdQuestion);
            setQuestionnaireQuestions(newQuestionsArray);
            return newQuestion;
        } catch (error) {
            console.error("Error adding question:", error);
            return null;
        }
    };

    /**
     * Delete a question
     */
    const deleteQuestion = async () => {
        const activeQuestionIndex = questionnaireQuestions.findIndex(q => q.id === activeQuestionId);
        if (questionnaireQuestions.length <= 1) {
            return false;
        }

        try {
            const isDeleted = await deleteQuestionnaireQuestionAPI({ questionId: activeQuestionId });
            if (isDeleted === true) {
                if (activeQuestionIndex !== 0) {
                    //find next questionId
                    const nextQuestion = questionnaireQuestions[activeQuestionIndex - 1];
                    setActiveQuestionId(get(nextQuestion, 'id', undefined));
                } else if (activeQuestionIndex === 0 && questionnaireQuestions.length > 1) {
                    //find next questionId
                    const nextQuestion = questionnaireQuestions[activeQuestionIndex + 1];
                    setActiveQuestionId(get(nextQuestion, 'id', undefined));
                } else {
                    setActiveQuestionId(undefined);
                }
                const newQuestions = [...questionnaireQuestions];
                newQuestions.splice(activeQuestionIndex, 1);
                setQuestionnaireQuestions(newQuestions);
                return true;
            }
            return false;
        } catch (error) {
            console.error("Error deleting question:", error);
            return false;
        }
    };

    /**
     * Duplicate a question
     * @returns {object|null} - Duplicated question or null if error
     */
    const duplicateQuestion = async () => {
        const activeQuestionIndex = questionnaireQuestions.findIndex(q => q.id === activeQuestionId);
        const questionToDuplicate = cloneDeep(questionnaireQuestions[activeQuestionIndex]);
        questionToDuplicate.position = activeQuestionIndex + 1;

        // Remove ids from options
        if (questionToDuplicate.options && Array.isArray(questionToDuplicate.options)) {
            questionToDuplicate.options = map(questionToDuplicate.options, option => {
                const { id, ...optionWithoutId } = option;
                return optionWithoutId;
            });
        } else {
            // This handles cases where options might be null (e.g., for RATING_SCALE, FREE_TEXT)
            questionToDuplicate.options = [];
        }

        try {
            const duplicatedQuestion = await createOrUpdateQuestionnaireQuestionAPI({
                signDocId: get(questionnaire, 'id'),
                questionId: undefined,
                isRandomizeAnswersOrder: questionToDuplicate.isRandomiseAnswersOrder,
                ratingScaleConfig: questionToDuplicate.ratingScaleConfig || null,
                ...questionToDuplicate
            });

            const newQuestions = [...questionnaireQuestions];
            newQuestions.splice(activeQuestionIndex + 1, 0, duplicatedQuestion);
            setQuestionnaireQuestions(newQuestions);
            return duplicatedQuestion;
        } catch (error) {
            console.error("Error duplicating question:", error);
            return null;
        }
    };

    /**
     * Update option text
     * @param {object} params - Parameters object
     * @param {string} params.optionId - Option ID
     * @param {string} params.questionId - Question ID
     * @param {string} params.value - New option text
     */
    const updateOptionText = ({ optionId, questionId, value }) => {
        const newQuestions = [...questionnaireQuestions];
        let question = newQuestions.find(q => q.id === questionId);
        const newOptions = [...question.options];
        let option = newOptions.find(option => option.id === optionId);
        option.text = value;
        question.options = newOptions;

        setQuestionnaireQuestions(newQuestions);
        setSaveQuestionnaireTriggerId(prev => prev + 1);
    };

    /**
     * Toggle correct answer for an option
     * @param {object} params - Parameters object
     * @param {string} params.optionId - Option ID
     * @param {string} params.questionId - Question ID
     */
    const toggleCorrectAnswer = ({ optionId, questionId }) => {
        const newQuestions = [...questionnaireQuestions];
        const question = newQuestions.find(q => q.id === questionId);
        const questionType = get(question, 'type', questionnaireQuestionTypes.MULTIPLE_CHOICE);
        
        let newOptions;

        if (questionType === questionnaireQuestionTypes.MULTIPLE_CHOICE) {
            // For multiple choice, only one option can be correct
            newOptions = question.options.map(option => ({
                ...option,
                isCorrect: option.id === optionId
            }));
        } else { // For CHECKBOX
            newOptions = question.options.map(option => {
                if (option.id === optionId) {
                    return { ...option, isCorrect: !option.isCorrect };
                }
                return option;
            });
        }
        
        question.options = newOptions;
        setQuestionnaireQuestions(newQuestions);
        handleServerSaveQuestion();
    };

    /**
     * Add a new option to the active question
     */
    const addOption = () => {
        const activeQuestionIndex = questionnaireQuestions.findIndex(q => q.id === activeQuestionId);
        const newQuestions = [...questionnaireQuestions];
        const newOptions = [...newQuestions[activeQuestionIndex].options];

        newOptions.push(createOption({ position: get(newOptions, 'length', 0) + 1 }));
        newQuestions[activeQuestionIndex].options = newOptions;
        handleServerSaveQuestion();
        setQuestionnaireQuestions(newQuestions);
    };

    /**
     * Remove an option from a question
     * @param {object} params - Parameters object
     * @param {string} params.questionId - Question ID
     * @param {string} params.optionId - Option ID
     * @returns {boolean} - Success status
     */
    const removeOption = ({ questionId, optionId }) => {
        const question = questionnaireQuestions.find(q => q.id === questionId);
        const questionIndex = questionnaireQuestions.findIndex(q => q.id === questionId);
        if (get(question, 'options.length', 0) <= 1) {
            return false;
        }

        const newQuestions = [...questionnaireQuestions];
        const newOptions = [...newQuestions[questionIndex].options];
        const optionIndex = newOptions.findIndex(option => option.id === optionId);
        newOptions.splice(optionIndex, 1);
        question.options = newOptions;
        setSaveQuestionnaireTriggerId(prev => prev + 1);
        setQuestionnaireQuestions(newQuestions);
        return true;
    };

    /**
     * Set active question
     * @param {string} questionId - Question ID
     */
    const setActiveQuestion = async (questionId) => {
        if (questionId !== activeQuestionId) {
            await handleServerSaveQuestion();
            setActiveQuestionId(questionId);
        }
    };

    const value = {
        // Questionnaire Creation State
        questionnaireQuestions,
        questionnaire,
        activeQuestionId,

        // Question pool management
        initQuestionPool,
        saveQuestionPool,
        setActiveQuestion,

        // Question management
        updateQuestion,
        addQuestion,
        deleteQuestion,
        duplicateQuestion,
        getQuestionById,

        // Option management
        updateOptionText,
        toggleCorrectAnswer,
        addOption,
        removeOption,
    };

    return (
        <QuestionnaireReactContext.Provider value={value}>
            <QuestionnaireSessionReactContextProvider>
                {children}
            </QuestionnaireSessionReactContextProvider>
        </QuestionnaireReactContext.Provider>
    );
};